import { type ChangeEvent, type ComponentProps, type ReactNode, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@nesso/ui/primitives/form';
import { Input } from '@nesso/ui/primitives/input';
import {
    InputGroup,
    InputGroupAddon,
    InputGroupInput,
    InputGroupText,
} from '@nesso/ui/primitives/input-group';
import { type MaskConfig, MaskedInputField } from '@nesso/ui/primitives/masked-input-field';

import { SettingsRow } from '@/shared/ui/settings/settings-row';

type BaseProps = {
    label: string;
    value: string | null;
    badge?: ReactNode;
    footer?: ReactNode;
    description?: string;
    // A visible label above the input, only when it must differ from `label`. Without it the
    // row heading stands in and the input keeps `label` as a screen-reader-only name.
    inputLabel?: string;
    isDisabled?: boolean;
    onEditingChange?: (isEditing: boolean) => void;
    // Applied in the input's `onChange`, before react-hook-form sees the value, so a typed
    // keystroke and a pasted string take the same path.
    inputFilter?: (value: string) => string;
    // Renders `MaskedInputField` instead of a bare input, which then owns the label and the error
    // message. The value it seeds from must already be in the mask's format.
    mask?: MaskConfig | string;
    // Overrides how the stored value renders at rest — e.g. `MaskedTaxId`. Falls back to the
    // plain value.
    renderValue?: (value: string) => ReactNode;
    // Defaults to `label`.
    placeholder?: string;
    inputMode?: ComponentProps<'input'>['inputMode'];
    maxLength?: number;
    inputClassName?: string;
    inputSuffix?: string;
};

// Both type parameters given explicitly: `z.ZodType`'s `Input` defaults to `unknown`, not
// `Output`, which otherwise breaks the `zodResolver` typing below.
type FieldSchema = z.ZodType<string, string>;

type Props = BaseProps &
    (
        | { onSave?: undefined; schema?: never }
        | {
              onSave: (value: string) => void | Promise<void>;
              schema: FieldSchema;
              isOptional?: false;
          }
        | {
              onSave: (value: string | null) => void | Promise<void>;
              schema: FieldSchema;
              isOptional: true;
          }
    );

type FormValues = { value: string };

export function SettingsEditableRow(props: Props) {
    const {
        label,
        value,
        badge,
        footer,
        description,
        inputLabel,
        isDisabled,
        onEditingChange,
        inputFilter,
        mask,
        renderValue,
        placeholder,
        inputMode,
        maxLength,
        inputClassName,
        inputSuffix,
    } = props;

    const [isEditing, setIsEditing] = useState(false);

    const isOptional = 'isOptional' in props && props.isOptional === true;
    const schema = 'schema' in props ? props.schema : undefined;

    // An optional field's empty save must bypass the schema rather than fail it — the schema
    // alone has no notion of "not provided".
    const resolver = schema
        ? zodResolver(z.object({ value: isOptional ? schema.or(z.literal('')) : schema }))
        : undefined;

    const toFieldValue = (raw: string) => (inputFilter ? inputFilter(raw) : raw);

    const form = useForm<FormValues>({
        resolver,
        defaultValues: { value: toFieldValue(value ?? '') },
    });

    const startEditing = () => {
        form.reset({ value: toFieldValue(value ?? '') });
        setIsEditing(true);
        onEditingChange?.(true);
    };

    const stopEditing = () => {
        setIsEditing(false);
        onEditingChange?.(false);
    };

    const handleSubmit = async (data: FormValues) => {
        if (!props.onSave) return;
        try {
            if (isOptional) {
                await props.onSave(data.value || null);
            } else {
                await props.onSave(data.value);
            }
        } catch {
            form.setError('value', { message: "Couldn't save. Try again." });
            return;
        }
        stopEditing();
    };

    const handleCancel = () => {
        form.reset({ value: toFieldValue(value ?? '') });
        stopEditing();
    };

    if (isEditing && props.onSave) {
        return (
            <FormProvider {...form}>
                <SettingsRow
                    label={label}
                    caption={description}
                    action={{ label: 'Cancel', onClick: handleCancel }}
                >
                    {mask ? (
                        <Controller
                            control={form.control}
                            name="value"
                            render={({ field, fieldState }) => (
                                <MaskedInputField
                                    {...field}
                                    mask={mask}
                                    label={inputLabel ?? label}
                                    labelClassName={cn(!inputLabel && 'sr-only')}
                                    error={fieldState.error?.message ?? false}
                                    placeholder={placeholder ?? label}
                                    inputMode={inputMode}
                                    className={inputClassName}
                                />
                            )}
                        />
                    ) : (
                        <FormField
                            control={form.control}
                            name="value"
                            render={({ field }) => {
                                const inputProps = {
                                    ...field,
                                    placeholder: placeholder ?? label,
                                    inputMode,
                                    maxLength,
                                    onChange: (event: ChangeEvent<HTMLInputElement>) =>
                                        field.onChange(toFieldValue(event.target.value)),
                                };
                                return (
                                    <FormItem className="gap-3">
                                        <FormLabel className={cn(!inputLabel && 'sr-only')}>
                                            {inputLabel ?? label}
                                        </FormLabel>
                                        {inputSuffix ? (
                                            <InputGroup className="w-fit">
                                                <FormControl>
                                                    <InputGroupInput
                                                        {...inputProps}
                                                        className={cn('flex-none', inputClassName)}
                                                        // Sized to the value rather than the row's full width;
                                                        // 1.25rem is the input's own 12px + 8px horizontal padding.
                                                        style={{
                                                            width: `calc(${Math.max(field.value.length, 1)}ch + 1.25rem)`,
                                                        }}
                                                    />
                                                </FormControl>
                                                <InputGroupAddon align="inline-end">
                                                    <InputGroupText>{inputSuffix}</InputGroupText>
                                                </InputGroupAddon>
                                            </InputGroup>
                                        ) : (
                                            <FormControl>
                                                <Input {...inputProps} className={inputClassName} />
                                            </FormControl>
                                        )}
                                        <FormMessage />
                                    </FormItem>
                                );
                            }}
                        />
                    )}
                    <div>
                        <Button
                            type="button"
                            size="lg"
                            onClick={form.handleSubmit(handleSubmit)}
                            disabled={form.formState.isSubmitting}
                        >
                            Save
                        </Button>
                    </div>
                </SettingsRow>
            </FormProvider>
        );
    }

    const actionLabel = value ? 'Edit' : 'Add';
    const displayValue = value ? (renderValue ? renderValue(value) : value) : 'Not provided';

    return (
        <SettingsRow
            label={label}
            caption={displayValue}
            badge={badge}
            action={
                props.onSave
                    ? {
                          label: actionLabel,
                          onClick: startEditing,
                          disabled: isDisabled,
                          ariaLabel: `${actionLabel} ${label.toLowerCase()}`,
                      }
                    : undefined
            }
            className={cn(isDisabled && 'opacity-50')}
        >
            {footer}
        </SettingsRow>
    );
}
